import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, TextField, Typography } from '@mui/material';
import { getTravelSettings, patchTravelSettings } from '../services/centralDbClient';

export default function AdminSettingsPage() {
  const [hoursPerTicket, setHoursPerTicket] = useState<number>(260);
  const [carAllowanceEuro, setCarAllowanceEuro] = useState<number>(50);
  const [initial, setInitial] = useState<{ hoursPerTicket: number; carAllowanceEuro: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      const s = await getTravelSettings();
      setHoursPerTicket(s.hoursPerTicket);
      setCarAllowanceEuro(s.carAllowanceEuro);
      setInitial({ hoursPerTicket: s.hoursPerTicket, carAllowanceEuro: s.carAllowanceEuro });
    })();
  }, []);

  const isValid = useMemo(() => {
    return Number.isFinite(hoursPerTicket) && hoursPerTicket > 0 && Number.isFinite(carAllowanceEuro) && carAllowanceEuro >= 0;
  }, [hoursPerTicket, carAllowanceEuro]);

  const isDirty = useMemo(() => {
    if (!initial) return false;
    return initial.hoursPerTicket !== hoursPerTicket || initial.carAllowanceEuro !== carAllowanceEuro;
  }, [initial, hoursPerTicket, carAllowanceEuro]);

  const onSave = async () => {
    if (!isValid || !isDirty) return;
    setSaving(true);
    try {
      const sanitizedHours = Math.max(1, Math.round(hoursPerTicket));
      const sanitizedCar = Math.max(0, Math.round(carAllowanceEuro * 100) / 100);
      await patchTravelSettings({ hoursPerTicket: sanitizedHours, carAllowanceEuro: sanitizedCar });
      setHoursPerTicket(sanitizedHours);
      setCarAllowanceEuro(sanitizedCar);
      setInitial({ hoursPerTicket: sanitizedHours, carAllowanceEuro: sanitizedCar });
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 3 }}>
      <Box sx={{ px: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Admin Settings</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {savedAt && !isDirty && (
            <Typography variant="caption" color="text.secondary">Saved</Typography>
          )}
          <Button variant="contained" onClick={onSave} disabled={!isValid || !isDirty || saving}>Save</Button>
        </Box>
      </Box>

      <Box sx={{ px: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Hours per ticket"
          type="number"
          value={hoursPerTicket}
          onChange={(e) => setHoursPerTicket(Number(e.target.value))}
          inputProps={{ min: '1', step: '1' }}
        />
        <TextField
          size="small"
          label="Car allowance (€)"
          type="number"
          value={carAllowanceEuro}
          onChange={(e) => setCarAllowanceEuro(Number(e.target.value))}
          inputProps={{ min: '0', step: '0.50' }}
        />
      </Box>
    </Container>
  );
}
